/* eslint-disable no-undef */
// Global flag to prevent duplicate renderProductInfo calls during color switch
window._colorSwitchInProgress = false;

function variantPicker(hasSupplier, switchSupplier, url, disableAutoSelect = false) {
  return {
    currentVariant: null,
    variantData: null,
    productsInSetData: null,
    options: [],
    updateUrl: true,
    url: "",
    section: "",
    isDropdown: false,
    loading: false,
    hasSupplier: false,
    switchSupplier: false,
    variantDataLoaded: false,
    isPreorder: false,
    ctaAtb: false,
    // Pre-computed bundle availability from Liquid (for native Shopify bundles)
    bundleShowPreorder: false,
    bundleSoldOut: false,
    bundleType: null, // 'native', 'custom', or null
    bundlePreorderMessage: null, // Pre-computed preorder message from Liquid

    get showPreorderButton() {
      if (!this.currentVariant) return false;

      // Don't show preorder if there's a custom CTA
      if (this.ctaAtb) return false;

      // For native bundles: use pre-computed value from Liquid
      if (this.bundleShowPreorder) return true;

      // For custom metafield bundles: check products in set inventory via JS
      if (this.productsInSetData && this.productsInSetData.isBundle) {
        const status = this.checkProductsInSetOutOfStock();
        return status.isOutOfStock && status.hasPreorder;
      }

      // For regular products: check variant's own inventory
      const hasPreorderTag = this.isPreorder;
      const isOutOfStock =
        this.currentVariant.inventory_quantity != null && this.currentVariant.inventory_quantity <= 0;
      const canSellWhenOutOfStock = this.currentVariant.inventory_policy === "continue";

      return hasPreorderTag && isOutOfStock && canSellWhenOutOfStock;
    },

    // Check if any product in set is out of stock based on current options
    // Returns { isOutOfStock: boolean, hasPreorder: boolean }
    checkProductsInSetOutOfStock() {
      if (!this.productsInSetData || !this.productsInSetData.productsInSet) {
        return { isOutOfStock: false, hasPreorder: false };
      }

      const productsInSet = this.productsInSetData.productsInSet;
      const selectedOptions = this.options.map((opt) => opt.value).filter((v) => v && v !== "Default Title");

      // Check if this is a single-variant bundle (no meaningful options selected)
      // This includes bundles where size options are links to different products
      const isSingleVariantBundle = selectedOptions.length === 0;

      // For single-variant bundles, check component nt.option1 === this.currentVariant.optio
      if (isSingleVariantBundle) {
        const bundleSize = this.getBundleSize();

        for (const productInSet of productsInSet) {
          let variantToCheck = null;

          // Use bundleType from Liquid instead of guessing from variant count
          // native = Shopify bundle app (each product has exactly 1 variant - the one in the bundle)
          // custom = custom metafield bundle (products have multiple variants, need to match by size)
          const isNativeBundle = this.bundleType === "native";

          if (isNativeBundle) {
            // Native bundle: use the single variant directly (it's the exact one in the bundle)
            variantToCheck = productInSet.variants[0];
          } else {
            // Custom bundle: need to match by size from URL
            if (bundleSize) {
              const sizeToMatchLower = bundleSize.toLowerCase();

              // Try to find variants that matches the bundle size
              const variantsToCheck = productInSet.variants?.filter((v) => {
                let option1Lower = (v.option1 || "").toLowerCase();
                let updatedSizeToMatchLower = sizeToMatchLower;

                // special handling for pillowcases to normalize size names for better matching
                if (productInSet.title.toLowerCase().includes("pillowcase")) {
                  if (option1Lower.includes("super king") || option1Lower.includes("emperor")) {
                    updatedSizeToMatchLower = "super king";
                  } else if (
                    option1Lower.includes("single") ||
                    option1Lower.includes("double") ||
                    option1Lower.includes("king")
                  ) {
                    updatedSizeToMatchLower = "standard";
                  }
                }
                // Check if option contains the size or size contains the option
                return (
                  option1Lower.includes(updatedSizeToMatchLower) ||
                  updatedSizeToMatchLower.includes(option1Lower.split(" /")[0].trim())
                );
              });

              // pick the first available variant that matches the size, or null if none available
              variantToCheck =
                variantsToCheck && variantsToCheck.length > 0
                  ? variantsToCheck.find((variant) => variant.available)
                  : null;
            }

            // Fallback to first variant if no match found
            if (!variantToCheck) {
              variantToCheck = productInSet.variants?.[0];
            }
          }

          if (variantToCheck) {
            const isOutOfStock = variantToCheck.inventory_quantity != null && variantToCheck.inventory_quantity <= 0;
            const isNotAvailable = variantToCheck.available === false;
            const hasPreorder = productInSet.hasPreorderTag || this.productsInSetData.hasPreorderTag;

            if (isOutOfStock || isNotAvailable) {
              return { isOutOfStock: true, hasPreorder: hasPreorder };
            }
          }
        }
        return { isOutOfStock: false, hasPreorder: false };
      }

      // For multi-variant bundles, match selected options to products in set
      for (const selectedValue of selectedOptions) {
        if (!selectedValue) continue;

        // Search all products in set for one with this option value
        for (const productInSet of productsInSet) {
          // Check if product title contains the selected value (e.g., "Product - Color - Size")
          const titleParts = (productInSet.title || "").split(" - ");
          const titleContainsValue = titleParts.some(
            (part) => part.trim().toLowerCase() === selectedValue.toLowerCase()
          );

          // Find variant matching the selected option value
          const matchingVariant = productInSet.variants?.find((v) => {
            return (
              v.option1 === selectedValue ||
              v.option2 === selectedValue ||
              v.option3 === selectedValue ||
              v.title === selectedValue
            );
          });

          // If title matches or we found a matching variant, check availability
          if (titleContainsValue || matchingVariant) {
            const variantToCheck = matchingVariant || productInSet.variants?.[0];

            if (variantToCheck) {
              const isOutOfStock = variantToCheck.inventory_quantity != null && variantToCheck.inventory_quantity <= 0;
              const isNotAvailable = variantToCheck.available === false;
              const hasPreorder = productInSet.hasPreorderTag || this.productsInSetData.hasPreorderTag;

              // If this product in set variant is out of stock, return the status
              if (isOutOfStock || isNotAvailable) {
                return { isOutOfStock: true, hasPreorder: hasPreorder };
              }
            }
          }
        }
      }

      return { isOutOfStock: false, hasPreorder: false };
    },

    // Get the selected bundle size from the checked radio button or extract from URL as fallback
    getBundleSize() {
      // First, try to get from the checked radio button (most reliable)
      const checkedRadio = this.$el?.querySelector('input[name="bundle_size_from_title"]:checked');
      if (checkedRadio?.value) {
        return checkedRadio.value;
      }

      // Fallback: extract from URL
      return this.extractBundleSizeFromUrl();
    },

    // Extract bundle size from URL by checking which child product sizes appear in the URL
    extractBundleSizeFromUrl() {
      const url = this.url || window.location.pathname;
      if (!url) return null;
      const urlLower = url.toLowerCase();

      // Get all unique sizes from child products' variants (option1 is typically the size)
      // Also extract sub-sizes from composite options like "AU King/NZ Super King"
      const allSizes = new Set();
      if (this.productsInSetData?.productsInSet) {
        for (const product of this.productsInSetData.productsInSet) {
          for (const variant of product.variants || []) {
            if (variant.option1) {
              allSizes.add(variant.option1);
              // Also add sub-sizes for composite options (e.g., "AU King/NZ Super King" -> "AU King", "NZ Super King")
              if (variant.option1.includes("/")) {
                const subSizes = variant.option1.split("/").map((s) => s.trim());
                subSizes.forEach((s) => allSizes.add(s));
              }
            }
          }
        }
      }

      // Convert sizes to URL-friendly format and check which one appears in the URL
      // Sort by length descending so longer/more specific sizes match first (e.g., "AU King" before "King")
      const sizesArray = [...allSizes].sort((a, b) => b.length - a.length);

      for (const size of sizesArray) {
        // Convert size to URL format (lowercase, spaces to hyphens)
        const sizeInUrl = size.toLowerCase().replace(/\s+/g, "-");

        // Use word boundary matching to avoid false positives (e.g., "king" matching in "au-king")
        // Check if the URL contains the size as a distinct segment (surrounded by hyphens or at start/end)
        const regex = new RegExp(`(^|-)${sizeInUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(-|$)`);
        if (regex.test(urlLower)) {
          return size;
        }
      }

      return null;
    },

    get variantIsAvailable() {
      if (!this.currentVariant) return false;

      // Don't show "Add to cart" if we're showing "Pre-order" button instead
      if (this.showPreorderButton) return false;

      // For native bundles: use pre-computed value from Liquid
      if (this.bundleSoldOut) return false;

      // For custom metafield bundles, check if any product in set is out of stock
      if (this.productsInSetData && this.productsInSetData.isBundle) {
        const productsInSetOutOfStock = this.checkProductsInSetOutOfStock();
        if (productsInSetOutOfStock.isOutOfStock) {
          return false; // Don't show "Add to cart" if any product in set is out of stock
        }
      }

      return this.currentVariant.available === true;
    },

    get variantIsNotAvailable() {
      if (!this.currentVariant) return false;

      // Don't show "Sold out" button if we're showing the "Pre-order" button instead
      if (this.showPreorderButton) {
        return false;
      }

      // Non-bundle: depend only on currentVariant (including supplier fallback)
      if (this.bundleType == null) {
        if (this.currentVariant.available === false && hasSupplier) {
          const allOption2 = this.variantData.filter(
            (variant) =>
              variant.option1 === this.currentVariant.option1 && variant.option3 === this.currentVariant.option3
          );
          const availabeOption2 = allOption2.find((variant) => variant.available === true);
          this.currentVariant = availabeOption2 || this.currentVariant; // Switch to available option 2 variant if exists
        }

        return this.currentVariant.available === false;
      }

      // Native bundle: use current variant's availability
      if (this.bundleType === "native") {
        return this.currentVariant.available === false;
      }

      // Custom metafield bundle: check if any product in set is out of stock without preorder
      if (this.productsInSetData && this.productsInSetData.isBundle) {
        const productsInSetOutOfStock = this.checkProductsInSetOutOfStock();
        if (productsInSetOutOfStock.isOutOfStock && !productsInSetOutOfStock.hasPreorder) {
          return true;
        }
      }

      if (this.currentVariant.available === false && hasSupplier) {
        const allFabrics = this.variantData.filter(
          (variant) =>
            variant.option1 === this.currentVariant.option1 && variant.option3 === this.currentVariant.option3
        );
        const availabeFabric = allFabrics.find((variant) => variant.available === true);
        this.currentVariant = availabeFabric || this.currentVariant; // Switch to available fabric variant if exists
      }

      // For native bundles: use pre-computed value from Liquid
      if (this.bundleSoldOut) {
        return true;
      }

      return this.currentVariant.available === false;
    },

    init() {
      this.hasSupplier = hasSupplier;
      this.switchSupplier = switchSupplier;

      // Get the section ID from the closest section element
      const sectionElement = this.$el.closest("[data-section]");
      if (sectionElement) {
        this.section = sectionElement.getAttribute("data-section");
      }

      // Get the product URL from the current page
      this.url = url || window.location.pathname;

      // Check if this is a preorder product
      this.isPreorder = this.$el.dataset.isPreorder === "true";

      // Check if product has custom CTA (cta_atb tag)
      this.ctaAtb = this.$el.dataset.ctaAtb === "true";

      // For bundles: read pre-computed availability from Liquid
      this.bundleShowPreorder = this.$el.dataset.bundleShowPreorder === "true";
      this.bundleSoldOut = this.$el.dataset.bundleSoldOut === "true";
      this.bundleType = this.$el.dataset.bundleType || null; // 'native', 'custom', or null
      this.bundlePreorderMessage = this.$el.dataset.bundlePreorderMessage || null;

      // Check if this is a parent product and fetch master child data
      const isParent = this.$el.dataset.isParent === "true";
      const masterChildUrl = this.$el.dataset.masterChildUrl;

      if (isParent && masterChildUrl) {
        // Store parent status for later use
        this.isParentProduct = true;
        this.masterChildUrl = masterChildUrl;
        // Fetch master child product data asynchronously
        this.fetchMasterChildData(masterChildUrl, sectionElement);
      } else {
        this.isParentProduct = false;
      }

      // Initialize floating button functionality
      this.initFloatingButton();

      // Parse variant data from x-ref
      try {
        const variantDataElement = this.$refs.variantData;
        if (variantDataElement) {
          this.variantData = JSON.parse(variantDataElement.textContent);
          this.variantDataLoaded = true;
        }
      } catch (error) {
        console.error("Error parsing variant data:", error.message);
        // Mark as loaded even on error to prevent infinite loading state
        this.variantDataLoaded = true;
      }

      // Parse products in set data for bundles
      try {
        const productsInSetElement = this.$refs.productsInSetData;
        if (productsInSetElement) {
          this.productsInSetData = JSON.parse(productsInSetElement.textContent);
        }
      } catch (error) {
        // Silently fail - not all products have products in set data
      }

      // Initialize options with empty values to prevent pre-selection
      // We'll determine the number of options from the variant data
      if (this.variantData && this.variantData.length > 0) {
        const firstVariant = this.variantData[0];

        // Guard against undefined firstVariant
        if (!firstVariant) {
          return;
        }

        // Count all option positions (including empty ones) for the options array
        const variantOptions = [firstVariant.option1, firstVariant.option2, firstVariant.option3];
        const totalOptionPositions = variantOptions.length;

        // Count only options that have values (not null/empty) for single-option detection
        const optionCount = variantOptions.filter((option) => option && option !== "").length;

        // Initialize options array with all positions (including empty ones)
        for (let i = 0; i < totalOptionPositions; i++) {
          this.options.push({ value: "" });
        }

        // Auto-populate options based on their semantic meaning, not position
        const optionNames = this.getOptionNames();

        // Auto-populate ALL options with the first available variant
        const firstAvailableVariant = this.variantData.find((variant) => variant.available);

        // Try to restore variant from URL parameters on page load
        let restoredFromURL = false;
        if (!disableAutoSelect) {
          const urlParams = new URLSearchParams(window.location.search);
          const variantIdFromURL = urlParams.get("variant");

          if (variantIdFromURL) {
            // Find the variant in the variant data
            const urlVariant = this.variantData.find((variant) => String(variant.id) === String(variantIdFromURL));

            if (urlVariant) {
              // Restore options from the URL variant
              const variantOptions = [urlVariant.option1, urlVariant.option2, urlVariant.option3];
              variantOptions.forEach((optionValue, index) => {
                if (optionValue && optionValue !== "" && this.options[index]) {
                  this.options[index].value = optionValue;
                }
              });

              // Set the current variant
              this.currentVariant = urlVariant;
              this.updateVariantInput();
              restoredFromURL = true;

              // Force Alpine.js reactivity by creating a new array reference
              this.options = [...this.options];

              // Use $nextTick to ensure DOM updates after Alpine processes the changes
              this.$nextTick(() => {
                // Force Alpine to re-evaluate by triggering a reactive update
                this.options = [...this.options];

                // Small delay to ensure radio buttons are rendered and Alpine has processed
                setTimeout(() => {
                  this.syncRadioButtons();
                }, 150);
              });
            }
          }
        }

        // If we restored from URL, ensure radio buttons stay synced
        if (restoredFromURL) {
          // Wait a bit longer to ensure all DOM is ready
          this.$nextTick(() => {
            setTimeout(() => {
              this.syncRadioButtons();
              // Sync again after a longer delay to catch any late DOM updates
              setTimeout(() => {
                this.syncRadioButtons();
              }, 300);
            }, 200);
          });
        }

        // When disableAutoSelect is true, read from DOM instead of auto-selecting
        if (disableAutoSelect) {
          // Ensure optionNames is available (it should be defined above, but guard against edge cases)
          const optionNamesForLookup = optionNames || this.getOptionNames() || [];

          // Read options from checked radio buttons within this variant picker element
          // Exclude navigation-only radios like bundle_size_from_title
          const checkedRadios = this.$el.querySelectorAll('input[type="radio"]:checked');

          checkedRadios.forEach((radio) => {
            // Skip navigation-only radios (they're not actual variant options)
            if (radio.name === "bundle_size_from_title") {
              return;
            }

            // Find which option index this radio belongs to
            const optionName = radio.name;
            const optionValue = radio.value;
            const optionIndex = optionNamesForLookup.findIndex(
              (name) => name.toLowerCase() === optionName.toLowerCase()
            );

            if (optionIndex >= 0 && this.options[optionIndex]) {
              this.options[optionIndex].value = optionValue;
            }
          });

          // For options that aren't selectable via radio buttons (like color/supplier/fabric),
          // auto-fill them from the variant data (preferably from an available variant)
          if (this.variantData?.length > 0) {
            // Try to use first available variant, fallback to first variant
            const firstVariant = this.variantData.find((v) => v.available) || this.variantData[0];
            const variantOptions = [firstVariant.option1, firstVariant.option2, firstVariant.option3];

            variantOptions.forEach((optionValue, index) => {
              // Only set if: 1) value exists, 2) option slot exists, 3) not already set from DOM
              if (optionValue && this.options[index] && !this.options[index].value) {
                this.options[index].value = optionValue;
              }
            });
          }

          // Now calculate the variant based on the selected options
          this.updateMasterId();

          if (!restoredFromURL) {
            this.ensurePreferredSupplierOrFallback();
          }

          // CRITICAL: If the variant is not available and we have a Supplier or Fabric option,
          // try switching to an available alternative automatically
          if (this.currentVariant && !this.currentVariant.available) {
            const optionNames = this.getOptionNames();
            const supplierIndex = optionNames.findIndex((name) => name.toLowerCase() === "supplier");
            const fabricIndex = optionNames.findIndex((name) => name.toLowerCase() === "fabric");

            // Try switching Supplier first (if exists)
            if (this.hasSupplier && supplierIndex >= 0) {
              const currentSupplier = this.options[supplierIndex]?.value;
              const alternativeSupplier = currentSupplier === "TCT" ? "PFL" : "TCT";

              // Check if alternative supplier makes this size available
              const alternativeVariant = this.variantData.find((variant) => {
                const variantOptions = [variant.option1, variant.option2, variant.option3];
                return this.options.every((opt, idx) => {
                  if (idx === supplierIndex) {
                    return variantOptions[idx] === alternativeSupplier;
                  }
                  return !opt.value || variantOptions[idx] === opt.value;
                });
              });

              if (alternativeVariant && alternativeVariant.available) {
                this.options[supplierIndex].value = alternativeSupplier;

                // Update the radio in DOM
                const supplierRadio = this.$el.querySelector(
                  `input[type="radio"][name="Supplier"][value="${alternativeSupplier}"]`
                );
                if (supplierRadio) {
                  // Clear all supplier radios first
                  this.$el.querySelectorAll('input[type="radio"][name="Supplier"]').forEach((r) => {
                    r.removeAttribute("checked");
                    r.checked = false;
                  });
                  // Check the alternative
                  supplierRadio.setAttribute("checked", "checked");
                  supplierRadio.checked = true;
                }

                // Recalculate variant with new supplier
                this.updateMasterId();
                return; // Exit early if supplier switch worked
              }
            }

            // Try switching Fabric (if exists and supplier switch didn't work)
            if (fabricIndex >= 0) {
              const currentFabric = this.options[fabricIndex]?.value;

              // Get all unique Fabric values
              const fabricValues = new Set();
              this.variantData.forEach((variant) => {
                const variantOptions = [variant.option1, variant.option2, variant.option3];
                if (variantOptions[fabricIndex]) {
                  fabricValues.add(variantOptions[fabricIndex]);
                }
              });

              // Try each Fabric value until we find an available variant
              for (const fabricValue of fabricValues) {
                if (fabricValue === currentFabric) continue; // Skip current

                const alternativeVariant = this.variantData.find((variant) => {
                  const variantOptions = [variant.option1, variant.option2, variant.option3];
                  return this.options.every((opt, idx) => {
                    if (idx === fabricIndex) {
                      return variantOptions[idx] === fabricValue;
                    }
                    return !opt.value || variantOptions[idx] === opt.value;
                  });
                });

                if (alternativeVariant && alternativeVariant.available) {
                  this.options[fabricIndex].value = fabricValue;

                  // Update the radio in DOM
                  const fabricRadio = this.$el.querySelector(
                    `input[type="radio"][name="Fabric"][value="${fabricValue}"]`
                  );
                  if (fabricRadio) {
                    // Clear all fabric radios first
                    this.$el.querySelectorAll('input[type="radio"][name="Fabric"]').forEach((r) => {
                      r.removeAttribute("checked");
                      r.checked = false;
                    });
                    // Check the alternative
                    fabricRadio.setAttribute("checked", "checked");
                    fabricRadio.checked = true;
                  }

                  // Recalculate variant with new fabric
                  this.updateMasterId();
                  return; // Exit early if fabric switch worked
                }
              }
            }
          }

          this.updateVariantInput();

          // Trigger product info update to show correct buttons, price, etc.
          if (this.currentVariant) {
            this.updatePriceFromCurrentVariant();

            // Only call renderProductInfo once during a color switch to prevent double fetching
            if (window._colorSwitchInProgress && !window._renderProductInfoCalled) {
              window._renderProductInfoCalled = true;
              this.renderProductInfo();
            } else if (!window._colorSwitchInProgress) {
              this.renderProductInfo();
            }
          }
        } else if (!restoredFromURL && firstAvailableVariant) {
          const variantOptions = [
            firstAvailableVariant.option1,
            firstAvailableVariant.option2,
            firstAvailableVariant.option3
          ];

          variantOptions.forEach((optionValue, index) => {
            if (optionValue && optionValue !== "") {
              this.options[index].value = optionValue;
            }
          });

          // Set the current variant to the first available one
          this.currentVariant = firstAvailableVariant;

          this.updateVariantInput();

          // Sync radio buttons after setting first available variant
          this.$nextTick(() => {
            setTimeout(() => {
              this.syncRadioButtons();
            }, 200);
          });
        } else {
          // Use original logic if no available variants for the product
          const colorOptionIndex = this.findOptionIndexByType(optionNames, ["color", "colour"]);

          // Special handling for single-option products
          if (
            optionCount === 1 &&
            firstVariant.option1 &&
            firstVariant.option2 === null &&
            firstVariant.option3 === null
          ) {
            // Get the actual option names from the DOM or variant data
            const actualOptionNames = this.getActualOptionNames();

            // Check if the single option is a color based on the option name
            const isColorOption =
              actualOptionNames[0] &&
              (actualOptionNames[0].toLowerCase().includes("color") ||
                actualOptionNames[0].toLowerCase().includes("colour"));

            if (isColorOption) {
              this.options[0].value = firstVariant.option1;
              this.currentVariant = firstVariant;
              this.updateVariantInput();
            } else {
              // For non-color single options, still auto-configure
              this.options[0].value = firstVariant.option1;
              this.currentVariant = firstVariant;
              this.updateVariantInput();
            }
          } else if (colorOptionIndex >= 0) {
            const colorValue = [firstVariant.option1, firstVariant.option2, firstVariant.option3][colorOptionIndex];

            if (colorValue) {
              this.options[colorOptionIndex].value = colorValue;

              // If this is the ONLY option (single-option product), set currentVariant immediately
              if (optionCount === 1) {
                this.currentVariant = firstVariant;
                this.updateVariantInput();
              }
            }
          }
        }

        // Auto-populate supplier option
        if (hasSupplier) {
          const supplier = switchSupplier ? "TCT" : "PFL";
          const supplierOptionIndex = this.findOptionIndexByType(optionNames, ["supplier"]);

          if (supplierOptionIndex >= 0) {
            this.options[supplierOptionIndex].value = supplier;
          }
        }

        // Auto-populate fabric option (switchSupplier + lager — se applyPreferredFabricFromSwitchSupplier)
        this.applyPreferredFabricFromSwitchSupplier(optionNames);

        // Ensure currentVariant is never null - fallback to first variant
        // But skip this if disableAutoSelect is true (we're reading from DOM)
        // Also skip if we restored from URL (don't override restored values)
        if (
          !disableAutoSelect &&
          !restoredFromURL &&
          !this.currentVariant &&
          this.variantData &&
          this.variantData.length > 0
        ) {
          this.currentVariant = this.variantData[0];

          // Populate options array with the selected variant's values
          const variantOptions = [
            this.currentVariant.option1,
            this.currentVariant.option2,
            this.currentVariant.option3
          ];

          variantOptions.forEach((optionValue, index) => {
            if (optionValue && optionValue !== "" && this.options[index]) {
              this.options[index].value = optionValue;
            }
          });

          this.updateVariantInput();
        }

        // Sync supplier and fabric options with SKU
        const option1And3 = this.variantData.filter(
          (variant) => variant.option1 === this.options[0].value && variant.option3 === this.options[2].value
        );
        const allOptions = option1And3.filter(
          (variant) => variant.option2 === this.options[1].value && variant.inventory_quantity > 0
        );

        // If supplier and fabric options is availabe, auto-select to the correct supplier and fabric
        if (allOptions.length > 0) {
          this.currentVariant = allOptions[0];
          this.updateVariantInput();
        }

        if (!restoredFromURL) {
          this.ensurePreferredSupplierOrFallback();
          this.applyPreferredFabricFromSwitchSupplier(optionNames);
          this.updateVariantInput();
        }

        // Final sync of radio buttons after all initialization is complete
        // This ensures radio buttons match the JavaScript state
        if (this.currentVariant) {
          this.$nextTick(() => {
            // Multiple sync attempts to catch any late DOM updates
            setTimeout(() => {
              this.syncRadioButtons();
            }, 400);
            setTimeout(() => {
              this.syncRadioButtons();
            }, 800);
            setTimeout(() => {
              this.syncRadioButtons();
            }, 1200);
          });
        }
      }
    },

    // Helper method to get actual option names from the product data
    getActualOptionNames() {
      try {
        const optionNamesElement = this.$refs.optionNames;
        if (optionNamesElement) {
          const optionNames = JSON.parse(optionNamesElement.textContent);
          return optionNames || [];
        }
      } catch (error) {
        console.error("Error parsing option names:", error.message);
      }
      return [];
    },

    // Helper method to get option names from the product data
    getOptionNames() {
      try {
        const optionNamesElement = this.$refs.optionNames;
        if (optionNamesElement) {
          const optionNames = JSON.parse(optionNamesElement.textContent);
          return (optionNames || []).map((name) => name.toLowerCase());
        }
      } catch (error) {
        console.error("Error parsing option names:", error.message);
      }
      return [];
    },

    /** Shopify `options_with_values` JSON (admin order of option values). */
    getOptionsWithValues() {
      try {
        const el = this.$refs.optionsWithValues;
        if (el?.textContent) {
          const parsed = JSON.parse(el.textContent);
          return Array.isArray(parsed) ? parsed : [];
        }
      } catch (error) {
        console.error("Error parsing optionsWithValues:", error.message);
      }
      return [];
    },

    /**
     * Option values for option1/2/3 index in the same order as Shopify admin (options_with_values).
     */
    getOrderedOptionValuesForIndex(optionIndex) {
      const opts = this.getOptionsWithValues();
      const byPosition = opts.find((o) => o && Number(o.position) === optionIndex + 1);
      const actualNames = this.getActualOptionNames();
      const wantName = actualNames[optionIndex];
      const entry =
        byPosition ||
        (wantName ? opts.find((o) => o && o.name && o.name.toLowerCase() === wantName.toLowerCase()) : null);

      if (!entry || !Array.isArray(entry.values) || entry.values.length === 0) {
        return [];
      }

      return entry.values
        .map((v) => {
          if (v == null || v === "") return null;
          if (typeof v === "string") return v;
          if (typeof v === "object" && v.name != null) return String(v.name);
          return String(v);
        })
        .filter(Boolean);
    },

    // Helper method to find option index by type
    findOptionIndexByType(optionNames, types) {
      for (let i = 0; i < optionNames.length; i++) {
        const optionName = optionNames[i];
        if (types.some((type) => optionName.includes(type))) {
          return i;
        }
      }
      return -1;
    },

    /**
     * Pick fabric from switchSupplier with stock fallback, for the *current* size (and color, supplier).
     * Uses isOptionAvailableAndInStock so primary/fallback is evaluated per variant combination.
     * First/second fabric follow Shopify admin order (options_with_values); fallback if JSON missing.
     */
    applyPreferredFabricFromSwitchSupplier(optionNames) {
      const fabricOptionIndex = this.findOptionIndexByType(optionNames, ["fabric"]);
      if (fabricOptionIndex < 0 || !this.variantData?.length) return;

      let fabricValuesArray = this.getOrderedOptionValuesForIndex(fabricOptionIndex);

      if (fabricValuesArray.length === 0) {
        const fabricValues = new Set();
        this.variantData.forEach((variant) => {
          if (variant.available || !this.variantData.some((v) => v.available)) {
            const variantOptions = [variant.option1, variant.option2, variant.option3];
            if (variantOptions[fabricOptionIndex]) {
              fabricValues.add(variantOptions[fabricOptionIndex]);
            }
          }
        });
        fabricValuesArray = Array.from(fabricValues);
      }

      if (fabricValuesArray.length === 0) return;
      const secondOrLastIdx = Math.min(1, fabricValuesArray.length - 1);
      const primaryIdx = this.switchSupplier ? secondOrLastIdx : 0;
      const fallbackIdx = this.switchSupplier ? 0 : secondOrLastIdx;

      const fabricInStock = (value) => this.isOptionAvailableAndInStock(fabricOptionIndex, value);

      let fabricValue = fabricValuesArray[primaryIdx];
      if (fabricValue && !fabricInStock(fabricValue)) {
        const alt = fabricValuesArray[fallbackIdx];
        if (alt) {
          fabricValue = alt;
        }
      }
      if (fabricValue && this.options[fabricOptionIndex]) {
        this.options[fabricOptionIndex].value = fabricValue;
        this.updateMasterId();
        this.options = [...this.options];
      }
    },

    /**
     * Prefer supplier from switchSupplier (TCT when true, PFL when false).
     * If that combination is sold out for the current selections, use the other supplier.
     */
    ensurePreferredSupplierOrFallback() {
      if (!this.hasSupplier || !this.variantData?.length) return;

      const optionNamesLower = this.getOptionNames();
      const supplierIdx = this.findOptionIndexByType(optionNamesLower, ["supplier"]);
      if (supplierIdx < 0 || !this.options[supplierIdx]) return;

      const preferred = this.switchSupplier ? "TCT" : "PFL";
      const fallback = preferred === "TCT" ? "PFL" : "TCT";

      const trySupplier = (supplier) => {
        this.options[supplierIdx].value = supplier;
        this.updateMasterId();
        return this.currentVariant && this.currentVariant.available === true;
      };

      if (!trySupplier(preferred)) {
        trySupplier(fallback);
      }

      this.options = [...this.options];
    },

    onVariantChange() {
      const optionNames = this.getOptionNames();
      // Per-size (per current options): prefer first/second fabric from admin order + lager; mirror init (supplier may change fabric availability).
      this.applyPreferredFabricFromSwitchSupplier(optionNames);
      this.ensurePreferredSupplierOrFallback();
      this.applyPreferredFabricFromSwitchSupplier(optionNames);
      this.updateMasterId();
      this.updateURL();
      this.updateVariantInput();
      this.updateOptionAvailability();

      if (this.currentVariant) {
        this.updatePriceFromCurrentVariant();
        this.renderProductInfo();
      }
    },

    onVariantSupplierChange(variantID, supplier) {
      this.updateMasterSupplierId(variantID, supplier);
      if (this.currentVariant) {
        const vo = [this.currentVariant.option1, this.currentVariant.option2, this.currentVariant.option3];
        vo.forEach((v, i) => {
          if (this.options[i] && v != null && v !== "") {
            this.options[i].value = v;
          }
        });
      }
      this.ensurePreferredSupplierOrFallback();
      this.updateURL();
      this.updateVariantInput();
      this.updateOptionAvailability();

      this.$nextTick(() => {
        this.syncRadioButtons();
      });

      if (this.currentVariant) {
        this.updatePriceFromCurrentVariant();
        this.renderProductInfo();
      }
    },

    updatePriceFromCurrentVariant() {
      if (!this.currentVariant || !this.section) return;
      applyVariantPriceToDom(this.section, this.currentVariant);
    },

    updateMasterSupplierId(variantID, supplier) {
      this.currentVariant = this.variantData.find(
        (v) => String(v.id) === variantID && (v.options[1] === supplier || v.options[2] === supplier)
      );
    },

    updateMasterId() {
      if (!this.variantData) {
        return;
      }
      // Use the options array populated by x-model bindings
      const selectedOptions = this.options.map((option) => option.value);

      // Find the first variant that matches the selected options
      // We need an exact match for all selected options
      const matchingVariant = this.variantData.find((variant) => {
        const variantOptions = [variant.option1, variant.option2, variant.option3];

        // Check if this variant matches the selected options
        const optionNames = this.getActualOptionNames();
        const fabricOptionIndex = this.findOptionIndexByType(optionNames, ["fabric"]);
        const supplierOptionIndex = this.findOptionIndexByType(optionNames, ["supplier"]);

        const matches = selectedOptions.every((selectedOption, index) => {
          // If no option is selected for this position, it should only match if the variant also has no option for this position
          if (!selectedOption || selectedOption === "") {
            // For hidden options (Supplier/Fabric), allow matching any value
            if (this.hasSupplier && index === supplierOptionIndex) {
              return true;
            }
            if (fabricOptionIndex >= 0 && index === fabricOptionIndex) {
              return true;
            }
            return !variantOptions[index] || variantOptions[index] === "";
          }
          // Otherwise, check if the variant option matches the selected option
          return variantOptions[index] === selectedOption;
        });

        // Check if we have at least one selected option
        const hasSelectedOptions = selectedOptions.some((option) => option && option !== "");

        // For single-option products, if only one option exists and it's selected, it should match
        if (this.options.length === 1 && hasSelectedOptions) {
          return true;
        }

        return matches && hasSelectedOptions;
      });

      if (matchingVariant) {
        this.currentVariant = matchingVariant;
      } else {
        console.warn("⚠️ [updateMasterId] No matching variant found:", {
          selectedOptions: selectedOptions,
          optionNames: this.getActualOptionNames(),
          fabricIndex: fabricOptionIndex >= 0 ? fabricOptionIndex : null,
          selectedFabric: fabricOptionIndex >= 0 ? selectedOptions[fabricOptionIndex] : null,
          supplierIndex: supplierOptionIndex >= 0 ? supplierOptionIndex : null,
          selectedSupplier: supplierOptionIndex >= 0 ? selectedOptions[supplierOptionIndex] : null
        });
        this.currentVariant = null;
      }
    },

    // Check if a specific option value is available given current selections
    isOptionAvailable(optionPosition, optionValue) {
      // If variant data isn't loaded yet, return true to prevent "sold out" flash
      if (!this.variantDataLoaded || !this.variantData) return true;

      // Create a copy of current selections
      const testOptions = [...this.options];
      testOptions[optionPosition] = { value: optionValue };

      // Check if there's any variant that matches this combination
      return this.variantData.some((variant) => {
        const variantOptions = [variant.option1, variant.option2, variant.option3];

        // Check if this variant matches the test options
        return testOptions.every((testOption, index) => {
          if (!testOption.value || testOption.value === "") {
            return !variantOptions[index] || variantOptions[index] === "";
          }
          return variantOptions[index] === testOption.value;
        });
      });
    },

    // Check if a specific supplier option value is available AND in stock
    isSupplierOptionAvailableAndInStock(optionPosition, optionValue) {
      // If variant data isn't loaded yet, return true to prevent "sold out" flash
      if (!this.variantDataLoaded || !this.variantData) return true;

      // Get current color selection
      const optionNames = this.getActualOptionNames();
      const colorOptionIndex = this.findOptionIndexByType(optionNames, ["color", "Color", "colour", "Colour"]);
      let currentColor = null;
      if (colorOptionIndex >= 0 && this.options[colorOptionIndex]) {
        currentColor = this.options[colorOptionIndex].value;
      }

      // Check if there's any available variant that matches this supplier and current color
      return this.variantData.some((variant) => {
        if (!variant.available) return false;

        // Check if variant title contains the supplier we're testing
        if (!variant.title || !variant.title.includes(optionValue)) {
          return false;
        }

        // If we have a color selection, check if variant matches that color too
        if (currentColor && !variant.title.includes(currentColor)) {
          return false;
        }

        return true;
      });
    },

    // Check if a specific option value is available AND in stock given current selections
    isOptionAvailableAndInStock(optionPosition, optionValue) {
      // If variant data isn't loaded yet, return true to prevent "sold out" flash
      if (!this.variantDataLoaded || !this.variantData) return true;

      // Check if this is a bundle product (check element ID or parent elements)
      let isBundleProduct = false;
      if (this.$el.id && this.$el.id.startsWith("bundle-selects")) {
        isBundleProduct = true;
      } else {
        // Check parent elements for bundle-selects ID
        let parent = this.$el.parentElement;
        while (parent && !isBundleProduct) {
          if (parent.id && parent.id.startsWith("bundle-selects")) {
            isBundleProduct = true;
            break;
          }
          parent = parent.parentElement;
        }
      }

      if (isBundleProduct) {
        // For bundle products, check if there's ANY available variant with this option value
        // regardless of other selections (since bundle options are independent)
        // For preorder products, also accept unavailable variants
        return this.variantData.some((variant) => {
          // Accept if variant is available OR if it's a preorder product
          if (!variant.available) return false;
          const variantOptions = [variant.option1, variant.option2, variant.option3];
          return variantOptions[optionPosition] === optionValue;
        });
      }

      // For regular products, check if there's an available variant that matches
      // the combination including this option value and currently selected other options
      const testOptions = [...this.options];
      testOptions[optionPosition] = { value: optionValue };

      // Get option names to identify which options are Fabric and Supplier
      const optionNames = this.getActualOptionNames();
      const fabricOptionIndex = this.findOptionIndexByType(optionNames, ["Fabric"]);
      const supplierOptionIndex = this.findOptionIndexByType(optionNames, ["Supplier"]);

      // If we're checking a size option and product has Fabric or Supplier,
      // check across all Fabric/Supplier values (since they're hidden and auto-selected)
      const isSizeOption = optionNames[optionPosition]?.toLowerCase().includes("size");
      if (isSizeOption && (fabricOptionIndex >= 0 || supplierOptionIndex >= 0)) {
        // Get all unique Fabric values from variants
        const fabricValues = new Set();
        if (fabricOptionIndex >= 0) {
          this.variantData.forEach((variant) => {
            const variantOptions = [variant.option1, variant.option2, variant.option3];
            if (variantOptions[fabricOptionIndex]) {
              fabricValues.add(variantOptions[fabricOptionIndex]);
            }
          });
        }

        // Get all unique Supplier values from variants
        const supplierValues = new Set();
        if (supplierOptionIndex >= 0) {
          this.variantData.forEach((variant) => {
            const variantOptions = [variant.option1, variant.option2, variant.option3];
            if (variantOptions[supplierOptionIndex]) {
              supplierValues.add(variantOptions[supplierOptionIndex]);
            }
          });
        }

        // Use arrays for iteration (empty array if option doesn't exist)
        const fabricValuesArray = fabricValues.size > 0 ? Array.from(fabricValues) : [null];
        const supplierValuesArray = supplierValues.size > 0 ? Array.from(supplierValues) : [null];

        // Check if the size is available in ANY combination of Fabric/Supplier options
        return fabricValuesArray.some((fabricValue) => {
          return supplierValuesArray.some((supplierValue) => {
            const testOptionsWithHidden = [...testOptions];
            if (fabricValue !== null && fabricOptionIndex >= 0) {
              testOptionsWithHidden[fabricOptionIndex] = { value: fabricValue };
            }
            if (supplierValue !== null && supplierOptionIndex >= 0) {
              testOptionsWithHidden[supplierOptionIndex] = { value: supplierValue };
            }

            return this.variantData.some((variant) => {
              // Accept if variant is available OR if it's a preorder product
              if (!variant.available) return false;

              const variantOptions = [variant.option1, variant.option2, variant.option3];

              // Check if this variant matches the test options (with this fabric/supplier combination)
              return testOptionsWithHidden.every((testOption, index) => {
                if (!testOption.value || testOption.value === "") {
                  return !variantOptions[index] || variantOptions[index] === "";
                }
                return variantOptions[index] === testOption.value;
              });
            });
          });
        });
      }

      // Standard check for non-size options or products without Fabric
      return this.variantData.some((variant) => {
        // Accept if variant is available OR if it's a preorder product
        if (!variant.available) return false;

        const variantOptions = [variant.option1, variant.option2, variant.option3];

        // Check if this variant matches the test options
        return testOptions.every((testOption, index) => {
          if (!testOption.value || testOption.value === "") {
            return !variantOptions[index] || variantOptions[index] === "";
          }
          return variantOptions[index] === testOption.value;
        });
      });
    },

    // Force update of option availability display
    updateOptionAvailability() {
      // This method triggers Alpine.js reactivity to update option availability
      // The actual availability checking is done by the isOptionAvailableAndInStock method
      // which is called by the Alpine.js expressions in the templates
      this.$nextTick(() => {
        // Force reactivity update by triggering a re-evaluation
        this.options = [...this.options];
      });
    },

    updateURL() {
      if (!this.updateUrl) return;

      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);

      if (this.currentVariant) {
        // If we have a full match, set the variant ID
        params.set("variant", this.currentVariant.id);
      } else {
        // If no full match, set individual option parameters
        this.options.forEach((option, index) => {
          if (option.value && option.value !== "") {
            params.set(`option${index + 1}`, option.value);
          }
        });
        // Remove variant param if no full match
        params.delete("variant");
      }

      window.history.replaceState({}, "", `${this.url}?${params.toString()}`);
    },

    updateVariantInput() {
      if (!this.currentVariant) return;

      // Find the form within the current section
      const section = this.$el.closest(`[data-section="${this.section}"]`);
      if (!section) return;

      // Find the main product form by ID first
      let form = section.querySelector(`#product-form-${this.section}`);

      // If not found, try finding by action attribute
      if (!form) {
        form = section.querySelector('form[action*="/cart/add"]');
      }

      // If still not found, try finding any form in the document (for products without options)
      if (!form) {
        form = document.querySelector(`#product-form-${this.section}`);
      }

      if (!form) return;

      const input = form.querySelector('input[name="id"]');

      if (input) {
        input.disabled = false;
        input.value = String(this.currentVariant.id);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    },

    renderProductInfo() {
      if (!this.currentVariant) return;

      // For parent products with master child: use master child URL since variant IDs belong to child
      const productUrl =
        this.isParentProduct && this.masterChildUrl ? this.masterChildUrl : this.url || window.location.pathname;

      // Use Shopify Section Rendering API for reliable variant-specific HTML
      // This ensures the section is rendered with the correct variant (price, etc.)
      const sectionsParam = `sections=${encodeURIComponent(this.section)}`;
      const variantParam = `variant=${this.currentVariant.id}`;
      const fetchUrl = `${productUrl}?${variantParam}&${sectionsParam}`;

      const priceElementId = `price-${this.section}`;

      fetch(fetchUrl)
        .then((response) => response.json())
        .then((data) => {
          // Section API returns sections at ROOT level: { "section-id": "html" }
          // Some themes/endpoints may use data.sections - check both
          let sectionHtml = data?.[this.section] ?? data?.sections?.[this.section];

          // Fallback: try "main" (template section key) if dynamic id not found
          if (!sectionHtml) {
            sectionHtml = data?.main ?? data?.sections?.main;
          }

          if (!sectionHtml) {
            // Try section_id=main (returns HTML directly) - works when sections param returns empty
            const sectionIdUrl = `${productUrl}?variant=${this.currentVariant.id}&section_id=main`;
            return fetch(sectionIdUrl)
              .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`section_id fetch failed: ${r.status}`))))
              .then((htmlText) => {
                if (htmlText && htmlText.trim().startsWith("<")) {
                  const html = new DOMParser().parseFromString(htmlText, "text/html");
                  return { doc: html, isFullPage: true };
                }
                throw new Error("Expected HTML from section_id");
              })
              .catch(() => {
                return fetch(`${productUrl}?variant=${this.currentVariant.id}`)
                  .then((r) => r.text())
                  .then((responseText) => {
                    const html = new DOMParser().parseFromString(responseText, "text/html");
                    return { doc: html, isFullPage: true };
                  });
              });
          }

          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHtml, "text/html");
          return { doc, isFullPage: false };
        })
        .then(({ doc }) => {
          // Target the VISIBLE price (--main block). getElementById returns the first match,
          // which is the hidden --top A/B block – we must update the visible one.
          const priceSelector = `.product-reviews-title-price--main #${CSS.escape(priceElementId)}`;

          // Update specific elements
          this.getElementsToRender().forEach((element) => {
            // For price: use visible block (--main); for others: getElementById
            const dest =
              element.id === priceElementId
                ? document.querySelector(priceSelector)
                : document.getElementById(element.id);
            const src = doc.getElementById(element.id);

            if (src && dest) {
              switch (element.type) {
                case "innerHTML":
                  dest.innerHTML = src.innerHTML;
                  break;
                case "value":
                  dest.value = src.value;
                  break;
              }
            }
          });

          // Update <title> tag and meta og: properties
          this.$nextTick(() => {
            // we assume that the product title is wrapped in a section ID
            const productTitleEl = document.getElementById(`product-title-${this.section}`);
            // and check that the colour swapping is actually enabled for the product
            if (productTitleEl && productTitleEl.dataset.colourAppended === "true") {
              const siteName = document.querySelector('meta[property="og:site_name"]')?.getAttribute("content");
              const titleText = productTitleEl.textContent.trim();
              document.title = siteName ? `${titleText} | ${siteName}` : titleText;

              const ogTitle = document.querySelector('meta[property="og:title"]');
              if (ogTitle) ogTitle.setAttribute("content", titleText);

              const ogUrl = document.querySelector('meta[property="og:url"]');
              if (ogUrl) {
                const existingOrigin = new URL(ogUrl.getAttribute("content")).origin;
                const url = new URL(this.url, existingOrigin);
                ogUrl.setAttribute("content", url.href);
              }
            }
          });

          // Fallback: if price element wasn't found, update entire product-purchase-info block
          // (handles edge cases where section ID format differs)
          const priceDestCheck = document.querySelector(priceSelector);
          if (!priceDestCheck || !doc.getElementById(priceElementId)) {
            const currentPurchaseInfo = document.querySelector(
              ".product-reviews-title-price--main #product-purchase-info"
            );
            const newPurchaseInfo = doc.querySelector(
              ".product-reviews-title-price--main #product-purchase-info, #product-purchase-info"
            );

            if (currentPurchaseInfo && newPurchaseInfo) {
              const clonedPurchaseInfo = newPurchaseInfo.cloneNode(true);
              currentPurchaseInfo.parentElement.replaceChild(clonedPurchaseInfo, currentPurchaseInfo);
            }
          }

          // Render sale badge on/off
          const container = document.querySelector(".badge-container");
          let badge = document.querySelector(".sale-badge");
          const compareAt = this.currentVariant.compare_at_price;
          const price = this.currentVariant.price;
          const isOnSale = compareAt && compareAt > price;

          if (!isOnSale) {
            if (badge) badge.remove();
          } else {
            const discountPercentage = Math.round(((compareAt - price) * 100) / compareAt);

            if (!badge && container) {
              badge = document.createElement("span");
              badge.className = "sale-badge p-gutter-4xs border-beige-100 bg-beige-100 border text-black";
              container.appendChild(badge);
            }

            if (badge) badge.textContent = `${discountPercentage}% Off`;
          }

          const newVariantPicker = doc.querySelector(".variantPicker");
          if (newVariantPicker) {
            this.bundleShowPreorder = newVariantPicker.dataset.bundleShowPreorder === "true";
            this.bundleSoldOut = newVariantPicker.dataset.bundleSoldOut === "true";
            this.bundlePreorderMessage = newVariantPicker.dataset.bundlePreorderMessage || null;
          }

          // Reinitialize floating button after DOM updates
          this.$nextTick(() => {
            this.initFloatingButton();
          });

          // Track variant change in Klaviyo if available
          if (window.klaviyo) {
            window.klaviyo.push([
              "track",
              "Changed Product Variant",
              {
                VariantId: this.currentVariant.id,
                Available: this.currentVariant.available
              }
            ]);
          }

          // Initialize Klaviyo BIS if available
          if (window.klaviyo?.backInStock?.init) {
            window.klaviyo.backInStock.init();
          }
        })
        .catch((error) => {
          console.error("Error updating variant information:", error);
        });
    },

    getElementsToRender() {
      return [
        {
          id: `price-${this.section}`,
          type: "innerHTML"
        },
        {
          id: `product-title-${this.section}`,
          type: "innerHTML"
        },
        {
          id: "variantID",
          type: "value"
        },
        {
          id: `addToCartWrapper-${this.section}`,
          type: "innerHTML"
        },
        {
          id: `ProductSubmitButton-wrapper`,
          type: "innerHTML"
        },
        {
          id: `ProductSubmitButton-fixed-wrapper`,
          type: "innerHTML"
        },
        {
          id: `inventory`,
          type: "innerHTML"
        },
        {
          id: `subscription`,
          type: "innerHTML"
        },
        {
          id: `product-small-comparison-${this.section}`,
          type: "innerHTML"
        },
        {
          id: `product-pricing-table-mobile-${this.section}`,
          type: "innerHTML"
        },
        {
          id: `product-pricing-table-desktop-${this.section}`,
          type: "innerHTML"
        },
        {
          id: `variant-selects-${this.section}`,
          type: "innerHTML"
        }
      ];
    },

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.$refs.variantData.textContent);
      return this.variantData;
    },

    getLowestVariantPrice() {
      if (!this.variantData || !Array.isArray(this.variantData) || this.variantData.length === 0) return null;

      // Find the lowest available price among all variants
      const availableVariants = this.variantData.filter((variant) => variant.available);

      if (availableVariants.length === 0) {
        // If no variants are available, use all variants
        return Math.min(...this.variantData.map((variant) => variant.price));
      }

      return Math.min(...availableVariants.map((variant) => variant.price));
    },

    initFloatingButton() {
      this.$nextTick(() => {
        const floatingButton = document.querySelector(".atc-floating-button");
        const regularButton = document.querySelector(".fixed-atc-button");
        const footer = document.querySelector(".shopify-section-group-footer-group");

        if (!floatingButton || !regularButton) return;

        if (this.floatingButtonCleanup) {
          this.floatingButtonCleanup();
        }

        const isDesktop = () => window.innerWidth >= 1024;

        let showFloatingTimeout = null;

        const clearShowFloatingTimeout = () => {
          if (showFloatingTimeout) {
            clearTimeout(showFloatingTimeout);
            showFloatingTimeout = null;
          }
        };

        const showFloating = () => {
          clearShowFloatingTimeout();

          showFloatingTimeout = setTimeout(() => {
            floatingButton.classList.add("atc-floating-button-visible");
            floatingButton.classList.remove("atc-floating-button-hidden");
            regularButton.classList.add("fixed-atc-button-hidden");
            regularButton.classList.remove("fixed-atc-button-visible");
          }, 100); // tweak as needed
        };

        const showRegular = () => {
          clearShowFloatingTimeout();

          floatingButton.classList.add("atc-floating-button-hidden");
          floatingButton.classList.remove("atc-floating-button-visible");
          regularButton.classList.add("fixed-atc-button-visible");
          regularButton.classList.remove("fixed-atc-button-hidden");
        };

        let isRegularVisible = false;
        let isFooterVisible = false;

        const update = () => {
          if (isDesktop()) {
            showRegular();
            return;
          }

          if (isFooterVisible) {
            showRegular();
            return;
          }

          if (isRegularVisible) {
            showRegular();
          } else {
            showFloating();
          }
        };

        const buttonObserver = new IntersectionObserver(
          (entries) => {
            isRegularVisible = entries[0].isIntersecting;
            update();
          },
          {
            root: null,
            threshold: 0,
            rootMargin: "0px 0px -67px 0px"
          }
        );

        buttonObserver.observe(regularButton);

        let footerObserver = null;

        if (footer) {
          footerObserver = new IntersectionObserver(
            (entries) => {
              isFooterVisible = entries[0].isIntersecting;
              update();
            },
            { threshold: 0 }
          );

          footerObserver.observe(footer);
        }

        const handleResize = () => update();
        window.addEventListener("resize", handleResize);

        update();

        this.floatingButtonCleanup = () => {
          clearShowFloatingTimeout();
          buttonObserver.disconnect();
          if (footerObserver) footerObserver.disconnect();
          window.removeEventListener("resize", handleResize);
        };
      });
    },

    /**
     * Fetch master child product data when on a parent product
     * Uses similar logic to color-switcher to update the DOM with master child data
     */
    async fetchMasterChildData(masterChildUrl, sectionElement) {
      try {
        // Fetch the master child product page
        const response = await fetch(masterChildUrl);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // Get the section ID
        const sectionId = sectionElement?.getAttribute("data-section");

        // Update variant data from master child
        // Try to find variant picker - could be regular or bundle
        let newVariantPickerWrapper = doc.querySelector(`[x-data*="variantPicker"][data-section="${sectionId}"]`);

        // If not found, try bundle selector
        if (!newVariantPickerWrapper) {
          newVariantPickerWrapper = doc.querySelector(`#bundle-selects-${sectionId}`);
        }

        // If still not found, try any element with variantPicker x-data
        if (!newVariantPickerWrapper) {
          newVariantPickerWrapper = doc.querySelector(`[x-data*="variantPicker"]`);
        }

        if (newVariantPickerWrapper) {
          // Extract variant data from the new document
          const newVariantDataScript = newVariantPickerWrapper.querySelector('script[x-ref="variantData"]');
          const newOptionNamesScript = newVariantPickerWrapper.querySelector('script[x-ref="optionNames"]');

          if (newVariantDataScript) {
            try {
              const masterChildVariantData = JSON.parse(newVariantDataScript.textContent);
              this.variantData = masterChildVariantData;
              this.variantDataLoaded = true;
              // Update the x-ref element in the current DOM
              const currentVariantDataScript = this.$refs.variantData;
              if (currentVariantDataScript) {
                currentVariantDataScript.textContent = newVariantDataScript.textContent;
              }
            } catch (error) {
              console.error("Error parsing master child variant data:", error);
            }
          }

          if (newOptionNamesScript) {
            try {
              // Update the x-ref element in the current DOM
              const currentOptionNamesScript = this.$refs.optionNames;
              if (currentOptionNamesScript) {
                currentOptionNamesScript.textContent = newOptionNamesScript.textContent;
              }
            } catch (error) {
              console.error("Error parsing master child option names:", error);
            }
          }

          const newOptionsWithValuesScript = newVariantPickerWrapper.querySelector('script[x-ref="optionsWithValues"]');
          if (newOptionsWithValuesScript) {
            try {
              const current = this.$refs.optionsWithValues;
              if (current) {
                current.textContent = newOptionsWithValuesScript.textContent;
              }
            } catch (error) {
              console.error("Error syncing master child optionsWithValues:", error);
            }
          }

          // Update variant selects section with master child's options
          const currentVariantSelects = sectionElement.querySelector(`#variant-selects-${sectionId}`);
          const docVariantSelects = doc.querySelector(`#variant-selects-${sectionId}`);

          if (currentVariantSelects && docVariantSelects) {
            // Clone the new variant selects
            const newVariantSelects = docVariantSelects.cloneNode(true);
            currentVariantSelects.parentElement.replaceChild(newVariantSelects, currentVariantSelects);
          }

          // Re-initialize options after updating variant data
          this.$nextTick(() => {
            if (this.variantData && this.variantData.length > 0) {
              // If we have selected options, try to find matching variant
              // Otherwise use first available variant
              let targetVariant = null;

              if (this.options && this.options.length > 0) {
                // Try to find variant matching current options
                const selectedOptions = this.options.map((opt) => opt.value);
                targetVariant = this.variantData.find((variant) => {
                  const variantOptions = [variant.option1, variant.option2, variant.option3];
                  return selectedOptions.every((selectedOption, index) => {
                    if (!selectedOption || selectedOption === "") {
                      return !variantOptions[index] || variantOptions[index] === "";
                    }
                    return variantOptions[index] === selectedOption;
                  });
                });
              }

              // Fallback to first available variant
              if (!targetVariant) {
                targetVariant = this.variantData.find((variant) => variant.available);
              }

              if (targetVariant) {
                // Update current variant to ensure it's from master child
                this.currentVariant = targetVariant;
                this.updateVariantInput();

                // Force Alpine.js reactivity
                this.options = [...this.options];

                // Sync radio buttons with the restored options
                setTimeout(() => {
                  const radioButtons = this.$el.querySelectorAll('input[type="radio"]');
                  const optionNamesArray = this.getOptionNames();

                  radioButtons.forEach((radio) => {
                    const optionIndex = optionNamesArray.findIndex(
                      (name) => name.toLowerCase() === radio.name.toLowerCase()
                    );
                    if (optionIndex >= 0 && this.options[optionIndex]) {
                      const shouldBeChecked = radio.value === this.options[optionIndex].value;
                      if (radio.checked !== shouldBeChecked) {
                        radio.checked = shouldBeChecked;
                        radio.dispatchEvent(new Event("change", { bubbles: true }));
                      }
                    }
                  });
                }, 150);

                // Update options if they're empty
                if (this.options.length === 0 || !this.options.some((opt) => opt.value)) {
                  const variantOptions = [targetVariant.option1, targetVariant.option2, targetVariant.option3];

                  variantOptions.forEach((optionValue, index) => {
                    if (optionValue && optionValue !== "") {
                      if (!this.options[index]) {
                        this.options.push({ value: "" });
                      }
                      this.options[index].value = optionValue;
                    }
                  });
                }

                // Force Alpine reactivity
                this.options = [...this.options];

                // Try to restore from URL after master child data is loaded
                this.restoreVariantFromURL();
              }
            }
          });
        }

        // Update product buttons/price section with master child data
        const currentButtons = sectionElement.querySelector('[id^="product-buttons"]');
        const newButtons = doc.querySelector('[id^="product-buttons"]');

        if (currentButtons && newButtons) {
          const parent = currentButtons.parentElement;
          const clonedButtons = newButtons.cloneNode(true);
          parent.replaceChild(clonedButtons, currentButtons);
        }

        // After master child data is loaded, try to restore from URL
        this.$nextTick(() => {
          setTimeout(() => {
            this.restoreVariantFromURL();
          }, 200);
        });
      } catch (error) {
        console.error("Error fetching master child product data:", error);
      }
    },

    /**
     * Restore variant from URL parameters and sync radio buttons
     */
    restoreVariantFromURL() {
      if (!this.variantData || this.variantData.length === 0) {
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const variantIdFromURL = urlParams.get("variant");

      if (variantIdFromURL) {
        // Find the variant in the variant data
        const urlVariant = this.variantData.find((variant) => String(variant.id) === String(variantIdFromURL));

        if (urlVariant) {
          // Restore options from the URL variant
          const variantOptions = [urlVariant.option1, urlVariant.option2, urlVariant.option3];
          variantOptions.forEach((optionValue, index) => {
            if (optionValue && optionValue !== "" && this.options[index]) {
              this.options[index].value = optionValue;
            }
          });

          // Set the current variant
          this.currentVariant = urlVariant;
          this.updateVariantInput();

          // Force Alpine.js reactivity
          this.options = [...this.options];

          // Sync radio buttons with restored options
          this.$nextTick(() => {
            setTimeout(() => {
              this.syncRadioButtons();
            }, 150);
          });
        }
      }
    },

    /**
     * Sync radio buttons with current options state
     */
    syncRadioButtons() {
      if (!this.options || this.options.length === 0) {
        return;
      }

      const radioButtons = this.$el.querySelectorAll('input[type="radio"]');
      if (radioButtons.length === 0) {
        return;
      }

      if (this.config?.disableRadioSync) return;

      const optionNamesArray = this.getOptionNames();

      // First, uncheck all radios to start fresh (except navigation-only radios)
      radioButtons.forEach((radio) => {
        // Skip bundle size navigation radios
        if (radio.name === "bundle_size_from_title") {
          return;
        }
        radio.checked = false;
      });

      // Then check the ones that match our options
      radioButtons.forEach((radio) => {
        // Skip bundle size navigation radios (they should keep their state)
        if (radio.name === "bundle_size_from_title") {
          return;
        }

        const optionIndex = optionNamesArray.findIndex((name) => name.toLowerCase() === radio.name.toLowerCase());
        if (optionIndex >= 0 && this.options[optionIndex] && this.options[optionIndex].value) {
          const shouldBeChecked = radio.value === this.options[optionIndex].value;
          if (shouldBeChecked) {
            radio.checked = true;
          }
        }
      });

      // Force Alpine reactivity by updating options array
      this.options = [...this.options];
    },

    async handleSubmit(event) {
      event.preventDefault();

      if (!this.currentVariant) {
        console.error("No currentVariant, aborting");
        return;
      }
      this.loading = true;

      try {
        const form = event.target;
        const variantId = String(this.currentVariant.id);
        const variantInput = form.querySelector('input[name="id"]');

        if (variantInput) {
          variantInput.disabled = false;
          variantInput.value = variantId;
        }

        const formData = new FormData(form);
        formData.set("id", variantId);

        const isParent = this.isParentProduct || this.$el.dataset.isParent === "true";
        if (isParent && !variantInput) {
          console.error("Parent product but no variant input found", {
            hasCurrentVariant: !!this.currentVariant,
            variantData: this.variantData?.length,
            formElements: Array.from(form.elements).map((el) => ({
              name: el.name,
              type: el.type,
              value: el.value
            }))
          });
        }

        // Use ShopSuey cart update function instead of basic fetch
        const response = await window.ShopSuey.cart.update({
          url: window.routes.cart_add_url,
          body: formData,
          context: this,
          onSuccess: (data) => {
            // Dispatch cart update event for the drawer
            window.dispatchEvent(
              new CustomEvent("cart-update", {
                detail: {
                  sections: data.sections
                }
              })
            );

            // Open cart drawer
            this.$dispatch("toggle-cart");

            // Close quick view if in quick view and restore scrolling
            if (window.Alpine && window.Alpine.store("quickview")) {
              window.Alpine.store("quickview").toggle(null);
              // Ensure scrolling is restored when quick view is closed via add to cart
              if (window.ShopSuey && window.ShopSuey.noScroll) {
                window.ShopSuey.noScroll(false);
              }
            }
          },
          onError: (error) => {
            console.error("Error adding to cart:", error);
          }
        });

        // Update sections to reflect changes immediately
        if (response?.sections) {
          window.ShopSuey.updateSections({ sections: response.sections }, this);
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
      } finally {
        this.loading = false;
      }
    }
  };
}

function getVisiblePriceSpan(priceWrap) {
  return [...priceWrap.querySelectorAll("span")].find(
    (el) => !el.classList.contains("sr-only") && !el.classList.contains("text-secondary-12-medium") && !el.closest("s")
  );
}

function formatVariantMoney(cents) {
  if (cents == null || cents === "") return "";

  const amount = Number(cents) / 100;

  if (window.Shopify?.formatMoney) {
    const format = window.theme?.moneyFormat || window.Shopify.money_format || "{{amount}}";
    let formatted = window.Shopify.formatMoney(cents, format);

    if (Number.isInteger(amount)) {
      formatted = formatted.replace(/(\d)[.,]00(?!\d)/, "$1");
    }

    return formatted;
  }

  const currency = window.Shopify?.currency?.active || "GBP";

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2
  }).format(amount);
}

function findMatchingVariantFromSelection(variantData, selectedOptions) {
  if (!variantData?.length) return null;

  const values = Object.values(selectedOptions).filter(Boolean);
  if (!values.length) return null;

  let match = variantData.find((variant) => {
    const opts = [variant.option1, variant.option2, variant.option3];
    return values.every((value) => opts.includes(value));
  });

  if (!match && selectedOptions.Size) {
    match =
      variantData.find(
        (variant) =>
          [variant.option1, variant.option2, variant.option3].includes(selectedOptions.Size) && variant.available
      ) ||
      variantData.find((variant) => [variant.option1, variant.option2, variant.option3].includes(selectedOptions.Size));
  }

  return match;
}

function applyVariantPriceToDom(sectionId, variant) {
  if (!sectionId || !variant) return;

  const priceWrap = document.querySelector(`.product-reviews-title-price--main #price-${sectionId} #price-wrap`);
  if (!priceWrap) return;

  const { price, compare_at_price: compareAt } = variant;
  const priceSpan = getVisiblePriceSpan(priceWrap);

  if (priceSpan) {
    priceSpan.textContent = formatVariantMoney(price);
  }

  const isOnSale = compareAt && compareAt > price;
  const compareEl = priceWrap.querySelector("s");

  if (isOnSale && compareEl) {
    compareEl.textContent = formatVariantMoney(compareAt);
  }
}

export { applyVariantPriceToDom, findMatchingVariantFromSelection };
export default variantPicker;
